import { initWasm } from "../../lib/wasm";

/** How long a start waits for the answer to «is there a newer `sw.js`?». */
const UPDATE_CHECK_MS = 3_000;
/** How long a newer worker gets to take the page over — the first page's own
 * wait for a worker. */
const TAKEOVER_MS = 15_000;

let initialization: Promise<void> | null = null;
export function initializeApplication(): Promise<void> {
  initialization ??= (async () => {
    if ("serviceWorker" in navigator) {
      const workers = navigator.serviceWorker;
      await withTimeout(workers.register("/sw.js"), "Service Worker registration");
      const registration = await withTimeout(workers.ready, "Service Worker activation");
      // ready means an active registration, not that this first page is controlled.
      if (workers.controller) {
        await adoptNewerWorker(workers, registration);
      } else {
        await new Promise<void>((resolve, reject) => {
          const clean = () => {
            clearTimeout(timer);
            workers.removeEventListener("controllerchange", change);
          };
          const change = () => {
            if (workers.controller) {
              clean();
              resolve();
            }
          };
          const timer = setTimeout(() => {
            clean();
            reject(new Error("Service Worker did not take control"));
          }, 15_000);
          workers.addEventListener("controllerchange", change);
          // A hard reload may bypass an already active worker. It will not
          // activate again, so explicitly ask it to claim this new page.
          registration.active?.postMessage({ t: "echofloat:claim" });
          change();
        });
      }
    }
    await withTimeout(initWasm(), "WebAssembly initialization");
  })().catch((error: unknown) => {
    initialization = null;
    throw error;
  });
  return initialization;
}

/**
 * A page an older worker already controls — the first load after a deploy
 * changed `sw.js` — would render under it while the browser's own update
 * check runs beside the page, and an older worker passes a prefix it was not
 * written for straight to the network, without the bearer: a sticker's poster
 * and bytes answered 401 and stood as «Sticker removed» (`/v1/stickers/`,
 * docs/stickers-plan.md D551). So the check is asked for here, bounded, and a
 * newer worker it finds — installing or waiting — gets the page before
 * anything renders; `skipWaiting` and `claim` make that a few hundred
 * milliseconds. Offline, slow or failing, the page goes on under the worker it
 * has: an update is never worth an error screen.
 */
async function adoptNewerWorker(
  workers: ServiceWorkerContainer,
  registration: ServiceWorkerRegistration,
): Promise<void> {
  const before = workers.controller;
  try {
    await withTimeout(registration.update(), "Service Worker update", UPDATE_CHECK_MS);
  } catch {
    return;
  }
  // A newer worker another tab's or the browser's own update began may be
  // past `waiting` already — active and still activating, its `claim()` not
  // yet run — and `update()` then finds nothing new.
  const activating = registration.active !== null && registration.active !== before;
  const incoming =
    registration.installing ?? registration.waiting ?? (activating ? registration.active : null);
  if (incoming === null || workers.controller !== before) return;
  await new Promise<void>((resolve) => {
    const done = () => {
      clearTimeout(timer);
      workers.removeEventListener("controllerchange", done);
      incoming.removeEventListener("statechange", settled);
      resolve();
    };
    // One that fails to install never takes over: nothing to wait for.
    const settled = () => {
      if (incoming.state === "redundant") done();
    };
    const timer = setTimeout(done, TAKEOVER_MS);
    workers.addEventListener("controllerchange", done);
    incoming.addEventListener("statechange", settled);
  });
}

function withTimeout<T>(operation: Promise<T>, label: string, ms = 15_000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    operation.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Module ownership survives StrictMode, but never an HMR disposal. */
export function createStartupLoader<T>(initialize: () => Promise<T>, start: () => void) {
  let cancelled = false;
  let loading: Promise<T> | null = null;
  return {
    load(): Promise<T> {
      if (cancelled) return Promise.reject(new DOMException("startup disposed", "AbortError"));
      loading ??= initialize()
        .then((application) => {
          if (cancelled) throw new DOMException("startup disposed", "AbortError");
          start();
          return application;
        })
        .catch((error: unknown) => {
          loading = null;
          throw error;
        });
      return loading;
    },
    cancel(): void {
      cancelled = true;
    },
  };
}
